import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { Text } from '../../atoms/text/text';
import { cn } from '../../lib/cn';
import { type LinkLike, NativeLink } from '../../lib/link';

const pageContainerVariants = cva('mx-auto w-full', {
	variants: {
		width: {
			xs: 'max-w-lg',
			narrow: 'max-w-2xl',
			medium: 'max-w-3xl',
			md: 'max-w-4xl',
			wide: 'max-w-7xl',
			full: 'max-w-none',
		},
	},
	defaultVariants: {
		width: 'wide',
	},
});

export interface PageContainerProps
	extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof pageContainerVariants> {}

/** Centers page content at a named max width. */
const PageContainer = React.forwardRef<HTMLDivElement, PageContainerProps>(
	({ className, width, ...props }, ref) => (
		<div ref={ref} className={cn(pageContainerVariants({ width, className }))} {...props} />
	),
);
PageContainer.displayName = 'PageContainer';

export interface PageBackLinkProps {
	/** Etichetta della destinazione, senza freccia: la aggiunge il componente. */
	label: React.ReactNode;
	href: string;
	/** Componente link da usare (default: link nativo `<a>`). */
	linkComponent?: LinkLike;
}

/**
 * Ritorno alla pagina di provenienza, sopra il titolo. Sostituisce il breadcrumb
 * di pagina: il mock `SLIMS-CER_v11.html` non ha percorsi di navigazione, solo il
 * singolo passo indietro.
 */
const PageBackLink = ({ label, href, linkComponent: Link = NativeLink }: PageBackLinkProps) => (
	<Link href={href} className="mb-1 inline-flex items-center gap-1.5 text-accent hover:underline">
		<Text as="span" variant="body" tone="inherit">
			← {label}
		</Text>
	</Link>
);
PageBackLink.displayName = 'PageBackLink';

export interface PageHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
	title: React.ReactNode;
	description?: React.ReactNode;
	/** Right-aligned action buttons / badges. */
	actions?: React.ReactNode;
	/** `compact` = small dashboard heading; `medium` = form-page heading; `display` = large 28px page heading. */
	size?: 'compact' | 'medium' | 'display';
	/** Impila titolo e azioni sugli schermi piccoli. */
	stackOnMobile?: boolean;
}

/** Page title block: title + optional description on the left, actions on the right. */
const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
	(
		{ className, title, description, actions, size = 'compact', stackOnMobile = false, ...props },
		ref,
	) => (
		<div
			ref={ref}
			className={cn(
				'mb-4 flex items-center justify-between gap-3',
				stackOnMobile && 'flex-col items-start sm:flex-row sm:items-center',
				className,
			)}
			{...props}
		>
			<div className="min-w-0">
				{size === 'display' ? (
					<h1 className="mb-1 text-[28px] font-extrabold leading-tight">{title}</h1>
				) : size === 'medium' ? (
					<h1 className="text-xl font-bold text-text">{title}</h1>
				) : (
					<Text as="h1" variant="title">
						{title}
					</Text>
				)}
				{description &&
					(size === 'display' ? (
						<p className="text-[15px] text-text-muted [&_span]:text-accent">{description}</p>
					) : size === 'medium' ? (
						<div className="text-xs uppercase text-text-muted">{description}</div>
					) : (
						<Text tone="muted" className="mt-0.5">
							{description}
						</Text>
					))}
			</div>
			{actions && <div className="flex shrink-0 items-center gap-3">{actions}</div>}
		</div>
	),
);
PageHeader.displayName = 'PageHeader';

export interface SidebarLayoutProps {
	/** Contenuto principale: anche un contesto container-query (`@container`), così le
	 * griglie al suo interno rispondono alla larghezza reale disponibile — sidebar
	 * inclusa — invece che alla larghezza della finestra. */
	main: React.ReactNode;
	/** Rail laterale a larghezza fissa. */
	sidebar: React.ReactNode;
}

/**
 * Contenuto principale + rail laterale: affiancati da `xl` in su (rail
 * sticky, 320px fissi), impilati sotto (rail sotto il main, mai nascosto) —
 * a differenza di un semplice `hidden xl:block`, il contenuto della sidebar
 * resta sempre raggiungibile quando la finestra si restringe.
 */
const SidebarLayout = ({ main, sidebar }: SidebarLayoutProps) => (
	<div className="flex flex-col gap-4 xl:flex-row xl:items-start">
		<div className="@container min-w-0 flex-1">{main}</div>
		{/* Senza rail (es. CER con l'AI spenta in MySlims) la colonna non resta
		    riservata: il contenuto prende tutta la larghezza. */}
		{sidebar != null && sidebar !== false && (
			<div className="w-full xl:sticky xl:top-0 xl:w-[320px] xl:shrink-0">{sidebar}</div>
		)}
	</div>
);
SidebarLayout.displayName = 'SidebarLayout';

/** Standalone display-size page title without bottom margin, for toolbar rows. */
const PageTitle = ({ children }: { children: React.ReactNode }) => (
	<h1 className="text-[28px] font-extrabold leading-tight">{children}</h1>
);
PageTitle.displayName = 'PageTitle';

/** Standalone display-size subtitle line (`page-sub` scale) for panels without a title. */
const PageSubtitle = ({ children }: { children: React.ReactNode }) => (
	<p className="mb-4 text-[15px] text-text-muted [&_span]:text-accent">{children}</p>
);
PageSubtitle.displayName = 'PageSubtitle';

export interface PageSectionProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
	title?: React.ReactNode;
	/** Right-aligned slot next to the section title (badge, counter, link). */
	aside?: React.ReactNode;
}

/** Titled block within a page; sections stack with a consistent vertical rhythm. */
const PageSection = React.forwardRef<HTMLDivElement, PageSectionProps>(
	({ className, title, aside, children, ...props }, ref) => (
		<section ref={ref} className={cn('min-w-0', className)} {...props}>
			{(title || aside) && (
				<div className="mb-2 flex items-center justify-between gap-2">
					{title && (
						<Text as="h2" variant="body" weight="bold">
							{title}
						</Text>
					)}
					{aside}
				</div>
			)}
			{children}
		</section>
	),
);
PageSection.displayName = 'PageSection';

export {
	PageBackLink,
	PageContainer,
	pageContainerVariants,
	PageHeader,
	PageSection,
	PageSubtitle,
	PageTitle,
	SidebarLayout,
};
